import { useCallback, useEffect, useState } from 'react';
import { App, AppFilter } from '../../types/app';
import { APIDataCollection, Metadata } from '../../network/types';
import { getApps } from '../../network/authApi/methods/System';

export const useApps = (pageChange?: number, searchQuery?: string, filters?: AppFilter | null) => {
  const [apps, setApps] = useState<Array<App>>([]);
  const [appsMetadata, setAppsMetadata] = useState<Metadata | undefined>();
  const [loadingApps, setLoadingApps] = useState(false);
  const [errorApps, setErrorApps] = useState(null);
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (pageChange) {
      setPage(pageChange);
    }
  }, [pageChange]);

  const loadMore = () => {
    setPage(page + 1);
  };

  useEffect(() => {
    setApps([]);
    setPage(1);
  }, [searchQuery, filters]);

  const retrieveApps = useCallback(
    (query: string, filters?: AppFilter | null) => {
      setLoadingApps(true);
      const params = new URLSearchParams();
      params.append('search', query);
      params.append('pagination', 'true');
      params.append('page', page.toString());
      params.append('nbResults', '30');
      const fields: Array<keyof App> = ['applicationName', 'isActive', 'id', 'overLocalNetwork'];
      fields.forEach(value => {
        params.append('fields[]', value);
      });

      if (filters?.active !== undefined) {
        params.append('filters[]', `isActive:eq:${filters.active}`);
      }

      if (filters?.overLocalNetwork !== undefined) {
        params.append('filters[]', `overLocalNetwork:eq:${filters.overLocalNetwork}`);
      }
      getApps(params.toString())
        .then((response: APIDataCollection) => {
          const result = Object.values(response['hydra:member']);

          setAppsMetadata({
            count: response['hydra:totalItems'],
            first: response['hydra:view']['hydra:first'],
            last: response['hydra:view']['hydra:first'],
            previous: response['hydra:view']['hydra:previous'],
            next: response['hydra:view']['hydra:next'],
          });
          setApps(l => [...l, ...result]);
        })
        .catch(error => {
          setErrorApps(error);
        })
        .finally(() => setLoadingApps(false));
    },
    [page],
  );

  useEffect(() => {
    let timeout: any;
    let cancel = false;
    if (!cancel) {
      setLoadingApps(true);
      if (searchQuery) {
        timeout = setTimeout(() => {
          retrieveApps(searchQuery, filters);
        }, 2000);
      } else {
        retrieveApps('', filters);
      }
    }

    return () => {
      clearTimeout(timeout);
      cancel = true;
      setLoadingApps(false);
    };
  }, [filters, retrieveApps, searchQuery]);

  return {
    apps,
    loadingApps,
    errorApps,
    appsMetadata,
    loadMore,
  };
};
